import type { Theme } from "@mui/material/styles";
import * as ReactChartJs from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import Color from "color";
import defaultTheme from "@/config/muiTheme";

type Registry = {
  pluginService?: { register: (_plugin: unknown) => void };
  defaults?: { global: any };
};
const registered = new WeakSet<object>();
const legendSpacing = {
  id: "hyper-legend-spacing",
  beforeInit(chart: { legend?: { afterFit?: () => void } }) {
    if (chart.legend)
      chart.legend.afterFit = function () {
        this.height += 30;
      };
  },
};
export function registerChartPlugins(registry?: Registry) {
  if (!registry?.pluginService || registered.has(registry)) return;
  registry.pluginService.register([ChartDataLabels, legendSpacing]);
  registered.add(registry);
}
const registry =
  "Chart" in ReactChartJs
    ? (ReactChartJs as unknown as { Chart?: Registry }).Chart
    : undefined;
registerChartPlugins(registry);

export function getDatasetDefaultOptions(theme: Theme) {
  return {
    hoverBackgroundColor: Color(theme.palette.primary.main).hex(),
    backgroundColor: Color(theme.palette.neutral[300]).lighten(0.1).hex(),
  };
}
export function getAreaDatasetDefaultOptions(theme: Theme) {
  const colors = getDatasetDefaultOptions(theme);
  return {
    backgroundColor: colors.backgroundColor,
    borderWidth: 2,
    borderColor: colors.hoverBackgroundColor,
    pointRadius: 5,
    pointHoverRadius: 6,
    pointHitRadius: 0,
    pointBackgroundColor: theme.palette.common.white,
    pointHoverBackgroundColor: colors.hoverBackgroundColor,
    hoverBorderWidth: 2,
    hoverBorderColor: colors.hoverBackgroundColor,
  };
}
export function getChartDefaultOptions(theme: Theme) {
  const backgroundColor = getDatasetDefaultOptions(theme).backgroundColor;
  return {
    defaultColor: backgroundColor,
    defaultFontColor: theme.palette.text.secondary,
    defaultFontFamily: theme.typography.fontFamily,
    tooltips: {
      backgroundColor: theme.palette.common.white,
      titleFontColor: theme.palette.text.primary,
      titleFontStyle: "normal",
      bodyFontColor: theme.palette.text.primary,
      bodyFontStyle: "bold",
      footerFontColor: theme.palette.text.primary,
      borderColor: theme.palette.divider,
      borderWidth: 1,
      displayColors: false,
    },
    elements: {
      point: { backgroundColor },
      line: { backgroundColor },
      rectangle: { backgroundColor },
      arc: { backgroundColor },
    },
  };
}
// Import-time configuration is brand-independent and never reads browser CSS.
if (registry?.defaults) {
  const defaults = registry.defaults.global;
  const options = getChartDefaultOptions(defaultTheme);
  Object.assign(
    defaults,
    {
      ...options,
      elements: Object.fromEntries(
        Object.entries(defaults.elements || {}).map(([key, element]) => [
          key,
          { ...(element as object), ...options.elements[key] },
        ])
      ),
      tooltips: { ...defaults.tooltips, ...options.tooltips },
    },
    {
      responsive: true,
      defaultFontSize: 10,
      animation: { ...defaults.animation, duration: 0 },
      hover: { mode: "nearest", animationDuration: 0 },
    }
  );
}
